import * as React from 'react';
import { AlertDialog as AlertDialogPrimitive } from 'radix-ui';
import { type VariantProps } from 'class-variance-authority';
import { useComposedRefs } from 'radix-ui/internal';
import { type LucideIcon } from 'lucide-react';
import { InsideDialogProvider } from '@/components/ui/inside-dialog-context';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const AlertDialog = AlertDialogPrimitive.Root;

const AlertDialogTrigger = AlertDialogPrimitive.Trigger;

const AlertDialogPortal = ({
  className,
  ...props
}: { className?: string } & AlertDialogPrimitive.AlertDialogPortalProps) => (
  <div className={cn(className)}>
    <AlertDialogPrimitive.Portal {...props} />
  </div>
);

const AlertDialogOverlay = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Overlay
    className={cn(
      'fixed inset-0 z-50 bg-white/80 backdrop-blur-xs data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 dark:bg-neutral-950/80',
      className,
    )}
    {...props}
    ref={ref}
  />
));
AlertDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName;

const AlertDialogContent = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content>
>(({ className, children, ...props }, ref) => {
  const [contentElement, setContentElement] = React.useState<HTMLDivElement | null>(null);

  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      <AlertDialogPrimitive.Content
        ref={useComposedRefs(ref, setContentElement)}
        className={cn(
          /**
           * grid-cols-[minmax(0,1fr)]: an auto track has a min-content floor, so long unbroken names spill past max-w-lg.
           * Centre with m-auto, not a transform, which would make this the containing block for fixed children.
           */
          'fixed inset-0 z-50 m-auto grid h-fit w-full max-w-lg grid-cols-[minmax(0,1fr)] gap-y-px border border-ui-void-gap bg-ui-void-gap shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          className,
        )}
        {...props}
        // NOTE: Closing the dialog doesn't remove pointer events from the body, so we are doing it manually.
        // Potential fix is to upgrade radixui dependencies
        // https://github.com/radix-ui/primitives/issues/1241
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          document.body.style.pointerEvents = '';
        }}
      >
        <InsideDialogProvider value={contentElement}>{children}</InsideDialogProvider>
      </AlertDialogPrimitive.Content>
    </AlertDialogPortal>
  );
});
AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName;

const AlertDialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className="flex min-h-12 flex-row items-center justify-between border-t border-t-toolbar-bevel bg-ui-medium px-2 py-3">
    <div className={cn(className)} {...props} />
  </div>
);
AlertDialogHeader.displayName = 'AlertDialogHeader';

const AlertDialogFooter = ({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-row bg-ui-medium sm:justify-end', className)} {...props}>
    <div className="flex w-full flex-col bg-ui-void-gap sm:h-12 sm:w-auto sm:flex-row sm:space-x-px sm:pl-px">
      {children}
    </div>
  </div>
);
AlertDialogFooter.displayName = 'AlertDialogFooter';

const AlertDialogTitle = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Title
    ref={ref}
    className={cn('text-sm font-semibold leading-none text-text-base', className)}
    {...props}
  />
));
AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName;

const AlertDialogDescription = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Description
    ref={ref}
    asChild
    className={cn('bg-ui-panel px-3 py-6 text-base text-text-gray', className)}
    {...props}
  >
    <div className={className}>{props.children}</div>
  </AlertDialogPrimitive.Description>
));
AlertDialogDescription.displayName = AlertDialogPrimitive.Description.displayName;

type AlertDialogButtonProps = {
  icon?: LucideIcon;
  iconClassName?: string;
  iconPosition?: 'left' | 'right';
};

const AlertDialogAction = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Action>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action> &
    AlertDialogButtonProps &
    Pick<VariantProps<typeof buttonVariants>, 'variant' | 'size'>
>(({ className, variant, icon: Icon, iconClassName, iconPosition = 'left', children, ...props }, ref) => {
  const iconElement = Icon && <Icon className={cn('icon-6', iconClassName)} />;

  return (
    <AlertDialogPrimitive.Action
      ref={ref}
      className={cn(buttonVariants({ variant }), Icon && 'gap-2', className)}
      {...props}
    >
      {iconPosition === 'left' && iconElement}
      {children}
      {iconPosition === 'right' && iconElement}
    </AlertDialogPrimitive.Action>
  );
});
AlertDialogAction.displayName = AlertDialogPrimitive.Action.displayName;

const AlertDialogCancel = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Cancel>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Cancel> & AlertDialogButtonProps
>(({ className, icon: Icon, iconClassName, iconPosition = 'left', children, ...props }, ref) => {
  const iconElement = Icon && <Icon className={cn('icon-6', iconClassName)} />;

  return (
    <AlertDialogPrimitive.Cancel
      ref={ref}
      className={cn(buttonVariants({ variant: 'secondary' }), Icon && 'gap-2', className)}
      {...props}
    >
      {iconPosition === 'left' && iconElement}
      {children}
      {iconPosition === 'right' && iconElement}
    </AlertDialogPrimitive.Cancel>
  );
});
AlertDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName;

export {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
};
